import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

type Theme = "light" | "dark" | "system"
type ResolvedTheme = "light" | "dark"

export interface ThemeProviderProps {
  children: ReactNode
  defaultTheme?: Theme
  storageKey?: string
  attribute?: "class" | "data-theme"
}

export interface ThemeContextValue {
  theme: Theme
  resolvedTheme: ResolvedTheme
  setTheme: (theme: Theme) => void
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined,
)

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light"
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light"
}

function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === "system" ? getSystemTheme() : theme
}

function applyTheme(resolved: ResolvedTheme, attribute: "class" | "data-theme") {
  if (typeof document === "undefined") return
  if (attribute === "class") {
    document.documentElement.classList.toggle("dark", resolved === "dark")
  } else {
    document.documentElement.setAttribute("data-theme", resolved)
  }
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "nexus_theme",
  attribute = "class",
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return defaultTheme
    const stored = localStorage.getItem(storageKey) as Theme | null
    return stored ?? defaultTheme
  })

  const resolvedTheme = useMemo(() => resolveTheme(theme), [theme])

  const setTheme = useCallback(
    (next: Theme) => {
      if (typeof window !== "undefined") {
        localStorage.setItem(storageKey, next)
      }
      setThemeState(next)
    },
    [storageKey],
  )

  // Apply theme to DOM whenever it changes
  useEffect(() => {
    applyTheme(resolveTheme(theme), attribute)
  }, [theme, attribute])

  // Listen for OS preference changes when in "system" mode
  useEffect(() => {
    if (typeof window === "undefined") return

    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    const handler = () => {
      if (theme === "system") {
        applyTheme(getSystemTheme(), attribute)
      }
    }
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [theme, attribute])

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  )

  return (
    <ThemeContext value={value}>
      {children}
    </ThemeContext>
  )
}
